interface HousekeepingTask {
  id: string;
  room_id: string;
  status: string;
  notes: string | null;
  created_at: string;
  rooms: { name: string } | null;
  assigned_staff: { id: string; full_name: string } | null;
}

interface CleaningOverviewProps {
  tasks: HousekeepingTask[];
}

const statusStyle = (status: string): { bg: string; text: string } => {
  switch (status) {
    case 'completed':
    case 'ready':
      return { bg: '#234638', text: '#FFFEFA' };
    case 'in_progress':
      return { bg: '#E8E0C8', text: '#234638' };
    case 'assigned':
      return { bg: '#F0EDE5', text: '#234638' };
    case 'waiting_inspection':
      return { bg: '#A8843A', text: '#FFFEFA' };
    case 'blocked':
      return { bg: '#A33A32', text: '#FFFEFA' };
    case 'paused':
      return { bg: '#F0EDE5', text: '#6B6B67' };
    case 'not_required':
      return { bg: '#F0EDE5', text: '#999' };
    default:
      return { bg: '#F0EDE5', text: '#6B6B67' };
  }
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' });
  } catch {
    return '--:--';
  }
}

export default function CleaningOverview({ tasks }: CleaningOverviewProps) {
  const completed = tasks.filter((t) => t.status === 'completed' || t.status === 'ready').length;
  const inProgress = tasks.filter((t) => t.status === 'in_progress').length;
  const pendingCount = tasks.filter((t) => t.status === 'not_started').length;
  const notRequired = tasks.filter((t) => t.status === 'not_required').length;
  const denominator = tasks.length - notRequired;
  const isEmpty = tasks.length === 0;

  return (
    <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: '#FFFEFA', borderColor: '#DED9CC' }}>
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #DED9CC' }}>
        <h3 className="text-sm font-semibold" style={{ color: '#252824' }}>清掃状況</h3>
        {!isEmpty && (
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#234638' }}></span>
              <span style={{ color: '#6B6B67' }}>完了 {completed}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#A8843A' }}></span>
              <span style={{ color: '#6B6B67' }}>作業中 {inProgress}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#DED9CC' }}></span>
              <span style={{ color: '#6B6B67' }}>未着手 {pendingCount}</span>
            </span>
            {notRequired > 0 && (
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#999' }}></span>
                <span style={{ color: '#999' }}>不要 {notRequired}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className="px-5 py-10 text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EDE5' }}>
            <i className="ri-brush-line text-xl" style={{ color: '#6B6B67' }}></i>
          </div>
          <p className="text-sm" style={{ color: '#6B6B67' }}>本日の清掃タスクはありません</p>
        </div>
      ) : (
        <>
          {/* Progress bar */}
          <div className="px-5 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium" style={{ color: '#252824' }}>進捗</span>
              <span className="text-xs" style={{ color: '#6B6B67' }}>{completed}/{denominator} 完了</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#F0EDE5' }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${denominator > 0 ? (completed / denominator) * 100 : 0}%`, backgroundColor: '#234638' }}
              ></div>
            </div>
          </div>

          {/* Task list */}
          <div className="p-5">
            <div className="space-y-2">
              {tasks.map((task) => {
                const style = statusStyle(task.status);
                const isDone = task.status === 'completed' || task.status === 'ready';
                const isWorking = task.status === 'in_progress';
                const isPending = task.status === 'not_started';
                return (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 p-3 rounded-lg transition-colors cursor-pointer"
                    style={{ backgroundColor: '#F6F4EE' }}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0EDE5' }}>
                      <span className="text-sm font-bold" style={{ color: '#234638' }}>
                        {task.rooms?.name ?? '--'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium" style={{ color: '#252824' }}>
                        {isDone ? '清掃完了' : isWorking ? '清掃中' : isPending ? '清掃待ち' : style.text}
                      </p>
                      <p className="text-xs" style={{ color: '#6B6B67' }}>
                        {formatTime(task.created_at)}
                        {task.assigned_staff && ` · ${task.assigned_staff.full_name}`}
                      </p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {!task.assigned_staff && task.status === 'not_started' && (
                        <button className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#234638', color: '#FFFEFA' }}>
                          割り当て
                        </button>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: style.bg, color: style.text }}>
                        {style.text === '#FFFEFA' ? (isDone ? '完了' : task.status === 'waiting_inspection' ? '点検待ち' : task.status === 'blocked' ? '対応必要' : task.status) : (task.status === 'not_started' ? '未着手' : task.status === 'assigned' ? '担当決定' : task.status === 'in_progress' ? '清掃中' : task.status === 'paused' ? '一時停止' : task.status === 'not_required' ? '不要' : task.status)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}